/**
 * Utility functions for campaign data operations
 */

import { extractDomainIdentifier } from './campaignGrouping';

// Cache for campaign type lookups to avoid redundant checks
const campaignTypeCache = {
  taboola: {},
  facebook: {}
};

// Clear cache when needed (e.g., when data reloads)
export const clearCampaignTypeCache = () => {
  Object.keys(campaignTypeCache.taboola).forEach(key => delete campaignTypeCache.taboola[key]);
  Object.keys(campaignTypeCache.facebook).forEach(key => delete campaignTypeCache.facebook[key]);
  console.log('Campaign type cache cleared');
};

// Check if a campaign is a Taboola campaign
export const isTaboolaCampaign = (campaignId, combinedData, taboolaCampaigns) => {
  if (!campaignId) {
    return false;
  }

  const campaignIdStr = String(campaignId || '');

  // Check cache first
  if (campaignTypeCache.taboola[campaignIdStr] !== undefined) {
    return campaignTypeCache.taboola[campaignIdStr];
  }

  // Limit logging to reduce console spam
  if (process.env.NODE_ENV !== 'production') {
    console.log(`Checking if ${campaignIdStr} is a Taboola campaign...`);
  }

  let result = false;
  
  // First check if the campaign exists in our data and has a platform property
  if (combinedData && combinedData.campaigns) {
    const campaign = combinedData.campaigns.find(c => c.campaign_id === campaignId);
    if (campaign) {
      // If platform is explicitly set, use that
      if (campaign.platform === 'taboola') {
        if (process.env.NODE_ENV !== 'production') {
          console.log(`${campaignId} has platform attribute "taboola" in combinedData`);
        }
        result = true;
      } else if (campaign.platform === 'facebook') {
        if (process.env.NODE_ENV !== 'production') {
          console.log(`${campaignId} has platform attribute "facebook" in combinedData`);
        }
        result = false;
      }
    }
  }
  
  // If we haven't determined the type yet, check other indicators
  if (result === false) {
    // Check if the campaign exists directly in the taboolaCampaigns
    if (taboolaCampaigns && taboolaCampaigns[campaignId]) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Direct match found: ${campaignId} exists in taboolaCampaigns`);
      }
      result = true;
    }
    // Look for the T- prefixed version
    else if (taboolaCampaigns && !campaignIdStr.startsWith('T-') && taboolaCampaigns[`T-${campaignIdStr}`]) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Prefixed match found: T-${campaignIdStr} exists in taboolaCampaigns`);
      }
      result = true;
    }
    // Only use numeric ID check as a last resort
    else if (/^\d+$/.test(campaignIdStr) && !campaignIdStr.startsWith('120225')) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`${campaignId} is a numeric ID without Facebook prefix, likely Taboola`);
      }
      result = true;
    }
  }
  
  // Cache the result for future lookups
  campaignTypeCache.taboola[campaignId] = result;
  
  if (process.env.NODE_ENV !== 'production' && !result) {
    console.log(`${campaignId} is NOT detected as a Taboola campaign`);
  }
  
  return result;
};

// Check if a campaign is a Facebook campaign
export const isFacebookCampaign = (campaignId, combinedData, facebookCampaigns) => {
  if (!campaignId) return false;

  const campaignIdStr = String(campaignId || '');

  // Check cache first
  if (campaignTypeCache.facebook[campaignIdStr] !== undefined) {
    return campaignTypeCache.facebook[campaignIdStr];
  }

  let result = false;

  // First check if it exists directly in Facebook campaigns
  if (facebookCampaigns && Object.keys(facebookCampaigns).length > 0) {
    if (facebookCampaigns[campaignIdStr]) {
      console.log(`${campaignIdStr} found in facebookCampaigns data from account ${facebookCampaigns[campaignIdStr].account_id || 'unknown'}`);
      result = true;
    }
  }

  // If not found in facebookCampaigns, check if it has the Facebook ID prefix (120225)
  if (!result && campaignIdStr.startsWith('120225')) {
    result = true;
  }
  
  // Check for known Facebook account IDs in the campaign data
  if (!result && combinedData && combinedData.campaigns) {
    const campaign = combinedData.campaigns.find(c => c.campaign_id === campaignId);
    if (campaign) {
      // Check if platform is explicitly set to facebook
      if (campaign.platform === 'facebook') {
        result = true;
      }
      // Check if account_id is from known Facebook accounts
      else if (campaign.account_id && ['979472960249107', '818932569781055', '10047721418585390'].includes(campaign.account_id)) {
        console.log(`${campaignId} identified as Facebook campaign based on account_id ${campaign.account_id}`);
        result = true;
      }
    }
  }
  
  // Cache the result for future lookups
  campaignTypeCache.facebook[campaignId] = result;
  
  return result;
};

// Pre-cache campaign types for better performance
export const precacheCampaignTypes = (campaigns, taboolaCampaigns, facebookCampaigns) => {
  if (!campaigns || !Array.isArray(campaigns)) return;
  
  console.log(`Pre-caching campaign types for ${campaigns.length} campaigns`);
  const combinedData = { campaigns };
  
  campaigns.forEach(campaign => {
    if (!campaign || !campaign.campaign_id) return;
    
    // Determine and cache Taboola status
    const isTaboola = isTaboolaCampaign(campaign.campaign_id, combinedData, taboolaCampaigns);
    campaignTypeCache.taboola[campaign.campaign_id] = isTaboola;
    
    // Determine and cache Facebook status
    const isFacebook = isFacebookCampaign(campaign.campaign_id, combinedData, facebookCampaigns);
    campaignTypeCache.facebook[campaign.campaign_id] = isFacebook;
  });
  
  console.log('Campaign type caching complete');
};

// Get Taboola campaign data
export const getTaboolaCampaignData = (campaignId, combinedData, taboolaCampaigns, isSubid3View) => {
  if (!campaignId || !combinedData || !combinedData.campaigns) return null;
  
  // Find this campaign in our data
  const campaign = combinedData.campaigns.find(c => c.campaign_id === campaignId);
  if (!campaign) {
    // If we have the ID in taboolaCampaigns, return that even without finding it in combinedData
    if (taboolaCampaigns && taboolaCampaigns[campaignId]) {
      return {
        ...taboolaCampaigns[campaignId],
        is_active: taboolaCampaigns[campaignId].is_active !== undefined ? 
          taboolaCampaigns[campaignId].is_active : true,
        status: taboolaCampaigns[campaignId].is_active !== undefined ? 
          (taboolaCampaigns[campaignId].is_active ? 'ACTIVE' : 'PAUSED') : 'ACTIVE',
        spent: 0
      };
    }
    
    return null;
  }
  
  // In subid3 view, we're looking at a subid3, not an actual campaign ID
  // So we need to find the parent subid2 campaign
  if (isSubid3View && campaign.subid2) {
    // Try to find the parent campaign by subid2
    const parentCampaign = taboolaCampaigns[campaign.subid2];
    if (parentCampaign) {
      return {
        ...parentCampaign,
        id: parentCampaign.id || campaign.subid2,
        is_active: parentCampaign.is_active !== undefined ? parentCampaign.is_active : true,
        status: parentCampaign.is_active !== undefined ? 
          (parentCampaign.is_active ? 'ACTIVE' : 'PAUSED') : 'ACTIVE',
        spent: campaign.cost || 0
      };
    }
  }
  
  // Check if this campaign exists directly in the taboolaCampaigns
  if (!taboolaCampaigns) {
    // Return a synthetic object using the combined data
    return {
      id: campaignId,
      name: campaign.campaign_name || campaignId,
      is_active: campaign.is_active !== undefined ? campaign.is_active : true,
      daily_cap: campaign.daily_cap || campaign.daily_budget || 0,
      status: campaign.is_active !== undefined ? (campaign.is_active ? 'ACTIVE' : 'PAUSED') : 'ACTIVE',
      spent: campaign.cost || 0,
      account_id: 'tnetwork-exm1-sc'
    };
  }
  
  const taboolaCampaign = taboolaCampaigns[campaignId];
  
  // If found directly, use all its properties
  if (taboolaCampaign) {
    // Make sure daily_cap is properly set
    const daily_cap = taboolaCampaign.daily_cap !== undefined && taboolaCampaign.daily_cap !== null
      ? parseFloat(taboolaCampaign.daily_cap)
      : (campaign.daily_cap || 0);
      
    // Use the campaign's is_active property to ensure it reflects the most current state
    const isActive = campaign.is_active !== undefined ? campaign.is_active : taboolaCampaign.is_active;
      
    return {
      ...taboolaCampaign,
      is_active: isActive,
      status: isActive ? 'ACTIVE' : 'PAUSED',
      daily_cap: daily_cap,
      spent: campaign.cost || 0
    };
  }
  
  // If not found directly, create a synthetic object with required fields
  const isActive = campaign.is_active !== undefined ? campaign.is_active : true;
  
  return {
    id: campaignId,
    name: campaign.campaign_name || campaignId,
    is_active: isActive,
    daily_cap: campaign.daily_cap || campaign.daily_budget || 0,
    status: isActive ? 'ACTIVE' : 'PAUSED',
    spent: campaign.cost || 0,
    account_id: 'tnetwork-exm1-sc'
  };
};

// ---------------------------------
// Facebook helpers
// ---------------------------------

/**
 * Return the most sensible daily budget for a Facebook campaign.
 * Priority order:
 *   1. daily_budget (campaign level)
 *   2. spend_cap     (campaign level)
 *   3. lifetime_budget (campaign level) –- rough daily by dividing by 30
 *   4. daily_cap or daily_budget coming from the blended report (combinedData)
 *   5. null (unknown)
 */
export const extractFacebookBudget = (fbCampaign = {}, combinedCampaign = {}) => {
  // 1) direct daily_budget
  if (fbCampaign.daily_budget !== undefined && fbCampaign.daily_budget !== null) {
    return parseFloat(fbCampaign.daily_budget);
  }

  // 2) spend_cap
  if (fbCampaign.spend_cap !== undefined && fbCampaign.spend_cap !== null) {
    return parseFloat(fbCampaign.spend_cap);
  }

  // 3) lifetime_budget -> approximate daily by 30-day month
  if (fbCampaign.lifetime_budget !== undefined && fbCampaign.lifetime_budget !== null) {
    return parseFloat(fbCampaign.lifetime_budget) / 30;
  }

  // 4) numbers coming from revenue/cost report
  if (combinedCampaign.daily_cap) {
    return parseFloat(combinedCampaign.daily_cap);
  }
  if (combinedCampaign.daily_budget) {
    return parseFloat(combinedCampaign.daily_budget);
  }

  // 5) nothing available
  return null;
};

// Get Facebook campaign data
export const getFacebookCampaignData = (campaignId, combinedData, facebookCampaigns) => {
  console.log(`🔍 getFacebookCampaignData called for: ${campaignId}`);
  console.log(`📊 Available Facebook campaigns:`, Object.keys(facebookCampaigns).slice(0, 5));
  
  // First try to get the campaign from the facebookCampaigns prop
  if (facebookCampaigns && facebookCampaigns[campaignId]) {
    console.log(`✅ Found exact match for ${campaignId} from account ${facebookCampaigns[campaignId].account_id}`);
    const campaign = facebookCampaigns[campaignId];
    
    // Extract budget information
    const budgetInfo = extractFacebookBudget(campaign);
    
    // Return the campaign data with budget information
    return {
      ...campaign,
      ...budgetInfo,
      // Ensure these fields are explicitly set
      id: campaign.id || campaignId,
      name: campaign.name || campaign.campaign_name || campaign.display_name || `Campaign ${campaignId}`,
      is_active: campaign.is_active !== undefined ? campaign.is_active : (campaign.status === 'ACTIVE'),
      status: campaign.status || (campaign.is_active ? 'ACTIVE' : 'PAUSED'),
      account_id: campaign.account_id,
      account_name: campaign.account_name
    };
  }
  
  // If no exact match, let's try to find it by checking if the IDs are stored differently
  if (facebookCampaigns && Object.keys(facebookCampaigns).length > 0) {
    console.log(`❌ No exact match for ${campaignId}. Checking for alternative formats...`);
    
    // Try to find by checking all campaign IDs in facebookCampaigns
    for (const [fbCampaignId, fbCampaign] of Object.entries(facebookCampaigns)) {
      // Check if this Facebook campaign ID matches our search in any way
      if (fbCampaignId === campaignId || 
          fbCampaign.id === campaignId ||
          fbCampaign.campaign_id === campaignId) {
        console.log(`✅ Found alternative match: ${fbCampaignId} -> ${campaignId}`);
        
        const budgetInfo = extractFacebookBudget(fbCampaign);
        
        return {
          ...fbCampaign,
          ...budgetInfo,
          id: fbCampaign.id || campaignId,
          name: fbCampaign.name || fbCampaign.campaign_name || `Campaign ${campaignId}`,
          is_active: fbCampaign.is_active !== undefined ? fbCampaign.is_active : (fbCampaign.status === 'ACTIVE'),
          status: fbCampaign.status || (fbCampaign.is_active ? 'ACTIVE' : 'PAUSED')
        };
      }
    }
    
    console.log(`❌ No alternative match found for ${campaignId}`);
    console.log(`📝 Sample Facebook campaign IDs:`, Object.keys(facebookCampaigns).slice(0, 3));
    console.log(`📝 Sample Facebook campaign objects:`, Object.values(facebookCampaigns).slice(0, 1).map(c => ({
      id: c.id,
      campaign_id: c.campaign_id,
      name: c.name
    })));
  }

  // Fallback to combinedData if facebookCampaigns is not available
  if (!combinedData || !combinedData.facebookCampaigns) {
    console.log(`❌ No facebookCampaigns data available for ${campaignId}`);
    return null;
  }

  // Try to find the campaign in the Facebook campaigns data
  const campaign = combinedData.facebookCampaigns[campaignId];
  
  if (!campaign) {
    console.log(`❌ Campaign ${campaignId} not found in combinedData.facebookCampaigns`);
    return null;
  }

  // Extract budget information
  const budgetInfo = extractFacebookBudget(campaign);
  
  // Return the campaign data with budget information
  return {
    ...campaign,
    ...budgetInfo,
    // Ensure these fields are explicitly set
    id: campaign.id || campaignId,
    name: campaign.name || campaign.campaign_name || `Campaign ${campaignId}`,
    is_active: campaign.is_active !== undefined ? campaign.is_active : (campaign.status === 'ACTIVE'),
    status: campaign.status || (campaign.is_active ? 'ACTIVE' : 'PAUSED')
  };
};

// Sort campaigns by a specific field - client-side function with performance optimizations
export const sortCampaigns = (campaigns, field, direction) => {
  if (!campaigns || !Array.isArray(campaigns) || campaigns.length === 0) {
    return campaigns;
  }
  
  // Performance optimization: pre-process values for sorting
  const startTime = performance.now();
  
  // Create a copy to avoid mutating the original array
  const sortedCampaigns = [...campaigns];
  
  // Special case for cost field
  if (field === 'cost') {
    const result = sortCampaignsByCost(sortedCampaigns, direction);
    const endTime = performance.now();
    console.log(`Sorting ${campaigns.length} campaigns by cost took ${(endTime - startTime).toFixed(2)}ms`);
    return result;
  }
  
  // Extract and convert values once for performance
  const sortValues = new Map();
  
  sortedCampaigns.forEach(campaign => {
    let value;
    
    // Special mappings for different field types
    if (field === 'campaign_id') {
      value = campaign.subid_3 || campaign.subid_2 || '';
    } 
    else if (field === 'impressions') {
      value = parseInt(campaign.impressions || campaign.visits || 0, 10);
    }
    else if (field === 'ctr') {
      // Handle CVR as a percentage and clean any string formatting
      let ctr = campaign.ctr;
      
      // If it's a string with %, remove the % sign
      if (typeof ctr === 'string') {
        ctr = ctr.replace('%', '');
      }
      
      value = parseFloat(ctr || 0);
    }
    else if (field === 'clicks') {
      value = parseInt(campaign.clicks || 0, 10);
    }
    else if (field === 'revenue') {
      value = parseFloat(campaign.revenue || campaign.estimated_revenue || 0);
    }
    else if (field === 'profit') {
      value = parseFloat(campaign.profit || 0);
    }
    else if (field === 'roas') {
      value = parseFloat(campaign.roas || 0);
    }
    else if (field === 'payout') {
      value = parseFloat(campaign.payout || campaign.epc || 0);
    }
    else if (field === 'cpc') {
      // Calculate CPC: Cost / Clicks
      const cost = parseFloat(campaign.cost || 0);
      const clicks = parseInt(campaign.clicks || 0, 10);
      value = clicks > 0 ? cost / clicks : 0;
    }
    else if (field === 'fb_ctr') {
      // Calculate Facebook CTR: Clicks / Impressions * 100
      if (campaign.platform === 'facebook') {
        const fbImpressions = parseInt(campaign.impressions || 0, 10);
        const fbClicks = parseInt(campaign.clicks || 0, 10);
        value = fbImpressions > 0 ? (fbClicks / fbImpressions) * 100 : 0;
      } else {
        value = 0; // Non-Facebook campaigns don't have CTR
      }
    }
    else {
      // Default handling for other fields
      value = campaign[field] || '';
    }
    
    // Store the extracted value
    sortValues.set(campaign, value);
  });
  
  // Perform the actual sort using our pre-calculated values
  const result = sortedCampaigns.sort((a, b) => {
    const valA = sortValues.get(a);
    const valB = sortValues.get(b);
    
    // For string comparison
    if (typeof valA === 'string' && typeof valB === 'string') {
      return direction === 'asc' 
        ? valA.localeCompare(valB) 
        : valB.localeCompare(valA);
    }
    
    // For numeric comparison
    if (!isNaN(valA) && !isNaN(valB)) {
      return direction === 'asc' ? valA - valB : valB - valA;
    }
    
    // Fallback for mixed types
    return direction === 'asc' 
      ? String(valA).localeCompare(String(valB)) 
      : String(valB).localeCompare(String(valA));
  });
  
  const endTime = performance.now();
  console.log(`Sorting ${campaigns.length} campaigns by ${field} took ${(endTime - startTime).toFixed(2)}ms`);
  
  return result;
};

// Sort campaigns by cost - specialized function with performance optimization
export const sortCampaignsByCost = (campaigns, direction = 'desc') => {
  if (!campaigns || !Array.isArray(campaigns) || campaigns.length === 0) {
    return campaigns;
  }
  
  const startTime = performance.now();
  
  // Pre-process costs for faster sorting
  const costValues = new Map();
  campaigns.forEach(campaign => {
    costValues.set(campaign, parseFloat(campaign.cost || 0));
  });
  
  // Sort based on pre-processed values
  const result = [...campaigns].sort((a, b) => {
    const costA = costValues.get(a);
    const costB = costValues.get(b);
    
    // Sort based on the direction
    return direction === 'desc' ? costB - costA : costA - costB;
  });
  
  const endTime = performance.now();
  console.log(`Cost-specific sorting of ${campaigns.length} campaigns took ${(endTime - startTime).toFixed(2)}ms`);
  
  return result;
};

// Calculate summary metrics from campaign data
export const calculateSummaryMetrics = (combinedData, platformFilter, startDate, endDate, facebookCampaigns = {}) => {
  const emptyDomainMetrics = {};

  if (!combinedData || !combinedData.campaigns || combinedData.campaigns.length === 0) {
    return {
      totalCampaigns: 0,
      totalRevenue: 0,
      totalCost: 0,
      totalProfit: 0,
      averageRoas: 0,
      averagePayout: 0,
      averageCpc: 0,
      totalClicks: 0,
      totalImpressions: 0,
      avgCtr: 0,
      dateRange: '0 days',
      totalActiveBudget: 0,
      domainMetrics: emptyDomainMetrics,
      platformCounts: {
        taboola: 0,
        facebook: 0,
        unknown: 0
      },
      platformMetrics: {
        taboola: {
          revenue: 0,
          cost: 0,
          profit: 0,
          roas: 0
        },
        facebook: {
          revenue: 0,
          cost: 0,
          profit: 0,
          roas: 0
        }
      }
    };
  }
  
  // Filter campaigns based on platform filter
  const filteredCampaigns = combinedData.campaigns.filter(campaign => 
    platformFilter === 'all' || campaign.platform === platformFilter
  );
  
  // Calculate totals
  const totalCampaigns = filteredCampaigns.length;
  let totalRevenue = 0;
  let totalCost = 0;
  let totalProfit = 0;
  let totalClicks = 0;
  let totalImpressions = 0;
  let totalConversions = 0;
  let totalFacebookClicks = 0;
  let totalTrafficLost = 0;
  let campaignsWithTrafficData = 0;
  
  // Platform-specific metrics
  const platformCounts = {
    taboola: 0,
    facebook: 0,
    unknown: 0
  };
  
  const platformMetrics = {
    taboola: {
      revenue: 0,
      cost: 0,
      profit: 0,
      roas: 0
    },
    facebook: {
      revenue: 0,
      cost: 0,
      profit: 0,
      roas: 0
    }
  };
  
  // Facebook account-specific metrics
  const facebookAccountMetrics = {
    '10047721418585390': {
      name: 'RSOC 1',
      campaigns: 0,
      revenue: 0,
      cost: 0,
      profit: 0,
      roas: 0
    },
    '979472960249107': {
      name: 'Search Arb 3',
      campaigns: 0,
      revenue: 0,
      cost: 0,
      profit: 0,
      roas: 0
    },
    '818932569781055': {
      name: 'Search Arb 1',
      campaigns: 0,
      revenue: 0,
      cost: 0,
      profit: 0,
      roas: 0
    }
  };

  // Domain-specific metrics
  const domainMetrics = {};
  let totalActiveBudget = 0;
  const countedCampaignIds = new Set(); // Track campaigns already counted for budget

  filteredCampaigns.forEach(campaign => {
    const platform = campaign.platform || 'unknown';
    
    // Increment platform count
    if (platformCounts[platform] !== undefined) {
      platformCounts[platform]++;
    } else {
      platformCounts.unknown++;
    }
    
    // Update global totals
    const revenue = parseFloat(campaign.revenue || 0);
    const cost = parseFloat(campaign.cost || 0);
    const profit = parseFloat(campaign.profit || 0);
    
    totalRevenue += revenue;
    totalCost += cost;
    totalProfit += profit;
    // Use demand_clicks (conversions) for summary metrics, not Facebook ad clicks
    totalClicks += parseInt(campaign.demand_clicks || campaign.clicks || 0, 10);
    totalImpressions += parseInt(campaign.impressions || 0, 10);
    
    // Calculate additional metrics for new summary cards
    totalConversions += parseInt(campaign.demand_clicks || campaign.clicks || 0, 10);
    if (platform === 'facebook') {
      totalFacebookClicks += parseInt(campaign.clicks || 0, 10); // Facebook ad clicks for CTR
      // Calculate traffic lost for this campaign (same logic as individual rows)
      const boughtClicks = parseInt(campaign.clicks || 0, 10); // Facebook ad clicks
      const visits = parseInt(campaign.demand_visits || 0, 10); // Actual visits to landing page
      if (boughtClicks > 0) {
        const trafficLostPercent = ((boughtClicks - visits) / boughtClicks) * 100;
        totalTrafficLost += trafficLostPercent;
        campaignsWithTrafficData++;
      }
    }
    
    // Update platform-specific metrics
    if (platform === 'taboola' || platform === 'facebook') {
      platformMetrics[platform].revenue += revenue;
      platformMetrics[platform].cost += cost;
      platformMetrics[platform].profit += profit;
      
      // For Facebook campaigns, also update account-specific metrics
      if (platform === 'facebook' && campaign.account_id && facebookAccountMetrics[campaign.account_id]) {
        console.log(`📊 Adding metrics for Facebook campaign ${campaign.campaign_id} to account ${campaign.account_id}:`, {
          revenue,
          cost,
          profit
        });
        facebookAccountMetrics[campaign.account_id].campaigns++;
        facebookAccountMetrics[campaign.account_id].revenue += revenue;
        facebookAccountMetrics[campaign.account_id].cost += cost;
        facebookAccountMetrics[campaign.account_id].profit += profit;
      } else if (platform === 'facebook') {
        console.log(`⚠️ Facebook campaign ${campaign.campaign_id} missing account_id or account not in metrics:`, {
          account_id: campaign.account_id,
          platform: campaign.platform,
          availableAccounts: Object.keys(facebookAccountMetrics),
          campaignData: {
            campaign_name: campaign.campaign_name,
            account_name: campaign.account_name
          }
        });
      }
    }

    // Track domain-specific metrics
    const domain = extractDomainIdentifier(campaign.campaign_name);
    if (domain && domain !== 'unknown') {
      if (!domainMetrics[domain]) {
        domainMetrics[domain] = {
          campaigns: 0,
          activeCampaigns: 0,
          revenue: 0,
          cost: 0,
          clicks: 0, // Facebook clicks
          conversions: 0, // Demand clicks
          budget: 0
        };
      }

      domainMetrics[domain].campaigns++;
      domainMetrics[domain].revenue += revenue;
      domainMetrics[domain].cost += cost;
      domainMetrics[domain].clicks += parseInt(campaign.clicks || 0, 10);
      domainMetrics[domain].conversions += parseInt(campaign.demand_clicks || 0, 10);

      // Track budget from facebookCampaigns (only for active campaigns, counted once)
      const fbCampaign = facebookCampaigns?.[campaign.campaign_id];
      if (fbCampaign?.is_active && !countedCampaignIds.has(campaign.campaign_id)) {
        const budget = parseFloat(fbCampaign.daily_budget || 0);
        domainMetrics[domain].budget += budget;
        domainMetrics[domain].activeCampaigns++;
        totalActiveBudget += budget;
        countedCampaignIds.add(campaign.campaign_id);
      }
    }
  });
  
  // Calculate platform-specific ROAS
  platformMetrics.taboola.roas = platformMetrics.taboola.cost > 0 
    ? platformMetrics.taboola.revenue / platformMetrics.taboola.cost 
    : 0;
    
  platformMetrics.facebook.roas = platformMetrics.facebook.cost > 0 
    ? platformMetrics.facebook.revenue / platformMetrics.facebook.cost 
    : 0;
    
  // Calculate Facebook account-specific ROAS
  Object.keys(facebookAccountMetrics).forEach(accountId => {
    const account = facebookAccountMetrics[accountId];
    account.roas = account.cost > 0 ? account.revenue / account.cost : 0;
  });

  // Calculate domain-specific derived metrics (ROAS, EPC, CVR)
  Object.keys(domainMetrics).forEach(domain => {
    const d = domainMetrics[domain];
    d.roas = d.cost > 0 ? d.revenue / d.cost : 0;
    d.epc = d.conversions > 0 ? d.revenue / d.conversions : 0;
    d.cvr = d.clicks > 0 ? (d.conversions / d.clicks) * 100 : 0;
  });

  // Calculate averages
  const avgCtr = totalImpressions > 0 ? (totalFacebookClicks / totalImpressions) * 100 : 0;
  const averageRoas = totalCost > 0 ? totalRevenue / totalCost : 0;
  const averagePayout = totalConversions > 0 ? totalRevenue / totalConversions : 0; // Fixed: use conversions
  const averageCpc = totalFacebookClicks > 0 ? totalCost / totalFacebookClicks : 0; // Use Facebook clicks for CPC
  const averageTrafficLost = campaignsWithTrafficData > 0 ? totalTrafficLost / campaignsWithTrafficData : 0;

  // Calculate date range in days
  const days = startDate && endDate
    ? Math.abs(Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))) + 1
    : 1;

  return {
    totalCampaigns,
    totalRevenue,
    totalCost,
    totalProfit,
    averageRoas,
    averagePayout,
    averageCpc,
    totalClicks: totalFacebookClicks, // Facebook ad clicks
    totalConversions, // Demand provider clicks (new)
    totalImpressions,
    avgCtr,
    averageTrafficLost, // New metric
    totalActiveBudget,
    domainMetrics,
    dateRange: `${days} days`,
    platformCounts,
    platformMetrics,
    facebookAccountMetrics
  };
}; 